/* ------------------------------------------------------------------ *
 * chr-infinite-scroll.js — Canada Hitch Rack
 *
 * Turns Dawn's numbered pagination into continuous loading on any page
 * that has a product grid: the next page is fetched and appended as the
 * shopper nears the end of the list.
 *
 * Finding the "next" link:
 *   Dawn's own class names are misleading here — in snippets/pagination
 *   the NEXT anchor carries `pagination__item--prev`. Rather than rely
 *   on that, this reads the current page from `data-page` on the
 *   pagination wrapper and looks for the link whose `page=` is exactly
 *   one higher. That keeps working if Dawn renames anything.
 *
 * The original pagination stays in the DOM, hidden. It is the fallback
 * with JavaScript off and it is what search engines follow, so removing
 * it would cost crawlable paths to deeper products.
 * ------------------------------------------------------------------ */

(function () {
  'use strict';

  var GRID = '#product-grid';
  var WRAP = '.pagination-wrapper';

  function pageOf(url) {
    var n = parseInt(new URL(url, window.location.origin).searchParams.get('page') || '1', 10);
    return isNaN(n) ? 1 : n;
  }

  /* The link to the page after `current`, or null when we're at the end.
     The pathname must match the list we are actually on: another script
     rewriting a pagination href to a different collection would otherwise
     make us append someone else's products, or stall on an empty page. */
  function nextUrl(scope, current, basePath) {
    var wrap = scope.querySelector(WRAP);
    if (!wrap) return null;
    var links = wrap.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      var u;
      try {
        u = new URL(href, window.location.origin);
      } catch (e) {
        continue;
      }
      if (u.pathname.replace(/\/+$/, '') !== basePath) continue;
      if (pageOf(href) === current + 1) return u.href;
    }
    return null;
  }

  function init() {
    var grid = document.querySelector(GRID);
    var wrap = document.querySelector(WRAP);
    if (!grid || !wrap) return;

    var current = parseInt(wrap.getAttribute('data-page') || '1', 10);
    if (isNaN(current)) current = 1;

    var basePath = window.location.pathname.replace(/\/+$/, '');
    var next = nextUrl(document, current, basePath);
    if (!next) return; // single page — nothing to do

    wrap.hidden = true;

    var host = document.createElement('div');
    host.className = 'chr-more';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'button button--secondary chr-more__btn';
    btn.textContent = 'Load more products';

    var live = document.createElement('p');
    live.className = 'chr-more__status';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');

    host.appendChild(btn);
    host.appendChild(live);
    wrap.parentNode.insertBefore(host, wrap);

    var loading = false;
    var done = false;
    var empties = 0;
    var MAX_EMPTY = 8;

    function load() {
      if (loading || done || !next) return;
      loading = true;
      btn.disabled = true;
      btn.textContent = 'Loading\u2026';

      fetch(next, { headers: { 'X-Requested-With': 'fetch' } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var incoming = doc.querySelector(GRID);
          if (!incoming) throw new Error('no grid in response');

          var items = incoming.children;
          var added = 0;
          /* Live HTMLCollection — appending removes the node from `items`,
             so keep taking the first child rather than walking an index. */
          while (items.length) {
            grid.appendChild(items[0]);
            added++;
          }

          current = current + 1;
          next = nextUrl(doc, current, basePath);
          loading = false;

          if (added) {
            empties = 0;
            live.textContent = added + ' more products loaded.';
          } else {
            empties++;
          }

          if (!next) {
            done = true;
            if (btn.parentNode) btn.parentNode.removeChild(btn);
            live.textContent = "That's everything.";
            if (observer) observer.disconnect();
            return;
          }

          btn.disabled = false;
          btn.textContent = 'Load more products';

          /* A page can legitimately render nothing: every product on it was
             sold out and the grid skipped them all. The sentinel will not
             fire again because the page did not grow, so pull the next one
             immediately. Capped so a long run of sold-out pages cannot turn
             into an unbounded fetch loop. */
          if (added === 0) {
            if (empties <= MAX_EMPTY) {
              load();
            } else {
              live.textContent = 'No more products in stock further down this list.';
            }
          }
        })
        .catch(function () {
          /* Leave the button usable and un-hide the real pagination so a
             shopper is never stranded halfway down a category. `done` stays
             false so the button can retry; the observer is disconnected so a
             persistent failure cannot retry on every scroll. */
          loading = false;
          if (observer) observer.disconnect();
          btn.disabled = false;
          btn.textContent = 'Load more products';
          wrap.hidden = false;
          live.textContent = "Couldn't load more — use the page numbers below.";
        });
    }

    btn.addEventListener('click', load);

    /* Auto-load as the end of the list approaches, so the shopper never has
       to press anything — the button stays only as a fallback.
       
       This observes the button row itself rather than a separate sentinel
       div. An empty <div> sentinel is invisible to IntersectionObserver
       here, because Dawn's base.css carries `div:empty { display:none }`
       and a display:none element never intersects anything. That silently
       disabled auto-loading entirely; the host always has content, so it
       cannot hit the same trap. */
    var observer = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        function (entries) {
          /* `some`, not entries[0] — the callback receives a batch and the
             intersecting entry is not always first. */
          if (entries.some(function (e) { return e.isIntersecting; })) load();
        },
        { rootMargin: '800px 0px' }
      );
      observer.observe(host);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
